import { forwardRef, useContext, useState } from "react";
import {
  Badge,
  Group,
  ActionIcon,
  Avatar,
  TextInput,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconX,
  IconSearch,
  IconPrinter,
  IconRefresh,
  IconMailForward,
} from "@tabler/icons-react";
import { openSendCampaignModal } from "./SendCampaignModal";
import { AppStatusBadge } from "./ClaimStatus";
import { web_createTicketSchema } from "@congrs/schemas";
import { ajv } from "@congrs/resolver";
import {
  TableActions,
  TableAction,
  EditAction,
  DeleteAction,
} from "../TableActions";
import { InlineEditableTable } from "../InlineEditableTable";
import {
  formatCustomFieldValue,
  BOOLEAN_OPTIONS,
} from "../../utils/customFieldValues";
import { selectableCountries } from "../CountrySelect";
import TicketSwitch from "./TicketSwitch";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useCreateTicket,
  useDeleteTicket,
  useUpdateTicket,
} from "../../api/tickets";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { useDataTableColumns } from "mantine-datatable";
import { confirmDuplicateName } from "./confirmDuplicateName";
import { useHasAvailableCampaigns, usePrintTicket } from "./ticketRowActions";
import { hasClaimFields } from "../../utils/ticketClaim";

// Same rule the Create Ticket modal validates against (web_createTicketSchema),
// so inline and modal validation can't drift apart.
const isValidEmail = ajv.compile(web_createTicketSchema.properties.email);

// Field types whose options are the same for every event
const FIXED_OPTIONS = {
  country: selectableCountries,
  boolean: BOOLEAN_OPTIONS,
};

const TicketsTable = forwardRef(
  (
    {
      data,
      pagination,
      sorting,
      isPending,
      filter,
      customColumns,
      columnsData,
    },
    ref
  ) => {
    const { user } = useContext(AuthContext);
    const currentWorkspace = getCurrentWorkspace(user);
    const currentEventId = currentWorkspace.eventId;
    const handlePrint = usePrintTicket(currentEventId);
    const [editingRow, setEditingRow] = useState({ ticketId: null });

    const { mutateAsync: updateTicket } = useUpdateTicket(
      {
        ticketId: editingRow.ticketId,
      },
      { skip: !editingRow.ticketId }
    );

    const { mutate: createTicket } = useCreateTicket();

    const hasAvailableCampaigns = useHasAvailableCampaigns(currentEventId);

    const { data: tagsData, isPending: tagsPending } =
      useGetPeopleTagsBySubtype({
        eventId: currentEventId,
        subtype: "categoryOfUser",
      });

    const defaultCategoryTag =
      tagsData?.find((tag) => String(tag.id) === filter.data?.category) ??
      tagsData?.find((tag) => tag.systemKey === "attendee");

    const tags = tagsData?.map((tag) => ({
      value: String(tag.id),
      label: tag.title,
    }));
    const { mutate: deleteTicket } = useDeleteTicket();
    const confirmDelete = useDeleteConfirm("ticket", deleteTicket);

    const saveRowAdd = (row) => {
      const {
        id,
        firstName,
        lastName,
        email,
        companyName,
        category,
        tags,
        ...rest
      } = row;

      const categoryOfUser = Array.isArray(category)
        ? category
        : category != null
          ? [category]
          : [];

      const attendee = {
        eventId: currentEventId,
        userData: {
          firstName: firstName,
          lastName: lastName,
          email: email || null,
          companyName: companyName || null,
          categoryOfUser,
          customFields: rest,
        },
      };

      confirmDuplicateName({
        eventId: currentEventId,
        firstName,
        lastName,
        onConfirm: () => createTicket(attendee),
      });
    };

    const saveRowEdit = async (row) => {
      setEditingRow({ ticketId: row.id });
      const originalRow = data.data?.find((r) => r.id === row.id);

      // call mutation to update the ticket
      const changedData = Object.keys(row).reduce((acc, key) => {
        if (row[key] !== originalRow[key]) {
          acc[key] = row[key];
        }
        return acc;
      }, {});

      const {
        category,
        email,
        firstName,
        lastName,
        companyName,
        ...customFields
      } = changedData;
      const holderData = {
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(companyName !== undefined && { companyName }),
      };
      try {
        if (
          category ||
          email ||
          Object.keys(holderData).length > 0 ||
          Object.keys(customFields).length > 0
        ) {
          await updateTicket({
            ...(category && { categoryOfUser: category }),
            ...(email && { email }),
            ...holderData,
            ...(Object.keys(customFields).length > 0 && { customFields }),
          });
        }
        setEditingRow({ ticketId: null });
      } catch (err) {
        console.error("Error on inline save:", err);
        throw new Error("Error on inline save");
      }
    };

    // Create a wrapper for setFilter that also cancels any active editing
    const handleFilterChange = (value) => {
      ref.current?.cancelEditing();
      filter.setFilter(value);
    };

    const visibleColumns = columnsData
      ?.filter((column) => column.isVisible === true)
      .map((column) => column.name);

    // Only once the API reports claims. Not in the column settings: those are
    // stored per event server-side, and only list the columns the API knows.
    const showClaimColumn = (data?.data ?? []).some(hasClaimFields);

    const tableColumns = [
      {
        accessor: "picture",
        width: "0%",
        editable: false,
        hidden: !visibleColumns?.includes("picture"),
        draggable: true,
        render: (row) => (
          <Group justify="center">
            <Avatar
              src={row?.picture}
              size="md"
              radius="sm"
              alt="avatar"
              color="initials"
              name={`${row?.firstName} ${row?.lastName}`}
            />
          </Group>
        ),
        align: "center",
      },
      {
        accessor: "firstName",
        editable: true,
        render: (row) => (
          <HolderCell
            value={row.firstName}
            accountValue={row.userId ? row.accountFirstName : null}
          />
        ),
        sortable: true,
        hidden: !visibleColumns?.includes("firstName"),
        draggable: true,
        inputProps: {
          placeholder: "First name",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="firstName"
          />
        ),
        filtering: filter?.data?.firstName,
      },
      {
        accessor: "lastName",
        sortable: true,
        editable: true,
        render: (row) => (
          <HolderCell
            value={row.lastName}
            accountValue={row.userId ? row.accountLastName : null}
          />
        ),
        hidden: !visibleColumns?.includes("lastName"),
        draggable: true,
        inputProps: {
          placeholder: "Last name",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="lastName"
          />
        ),
        filtering: filter?.data?.lastName,
      },
      {
        accessor: "email",
        sortable: true,
        editable: true,
        render: (row) =>
          !row.email ? (
            <Text size="sm" c="dimmed">
              No email
            </Text>
          ) : row.userId ? (
            <HolderCell value={row.email} accountValue={row.accountEmail} />
          ) : (
            <>
              <Text size="sm">{row.email}</Text>
              <Text size="xs" c="dimmed">
                No account
              </Text>
            </>
          ),
        hidden: !visibleColumns?.includes("email"),
        draggable: true,
        inputProps: {
          placeholder: "Email",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="email"
          />
        ),
        filtering: filter?.data?.email,
      },
      {
        accessor: "claim",
        title: "App",
        // The longest status, "Locked out", in full.
        width: 130,
        editable: false,
        draggable: true,
        hidden: !showClaimColumn,
        render: (row) => row.id !== "temp" && <ClaimCell row={row} />,
      },
      {
        accessor: "companyName",
        title: "Company",
        sortable: true,
        editable: true,
        draggable: true,
        inputProps: {
          placeholder: "Company",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="companyName"
          />
        ),
        filtering: filter?.data?.companyName,
      },
      {
        accessor: "category",
        draggable: true,
        render: (row) => {
          const tags = row?.tags?.filter(
            (tag) => tag.subtype === "categoryOfUser"
          );

          if (tags?.length === 0 || !tags) {
            return (
              <Text c="gray" size="sm">
                No category
              </Text>
            );
          }

          return tags?.map((tag) => (
            <Badge color={tag.color} variant="light" key={tag.id}>
              {tag.title}
            </Badge>
          ));
        },
        editable: true,
        hidden: !visibleColumns?.includes("category"),
        inputProps: {
          placeholder: "Category",
        },
        type: "tags",
        options: tags,
        filter: (
          <CategoryFilter
            query={filter.data}
            setQuery={handleFilterChange}
            tags={tags}
            isPending={tagsPending}
          />
        ),
        filtering: filter?.data?.category,
      },
      {
        accessor: "checkedInAt",
        title: "Checked in",
        width: "0%",
        sortable: true,
        editable: false,
        hidden: !visibleColumns?.includes("checkedInAt"),
        draggable: true,
        render: (row) =>
          row.id !== "temp" && (
            <Group justify="center">
              <TicketSwitch
                ticketId={row.id}
                checked={Boolean(row.checkedInAt)}
                columnName="checkedInAt"
              />
            </Group>
          ),
      },
      ...(customColumns || []).map((column) => {
        return {
          accessor: column.name,
          editable: true,
          hidden: !column.isVisible,
          draggable: true,
          render: (row) =>
            formatCustomFieldValue(
              Object.hasOwn(row, column.name) ? row[column.name] : undefined,
              column
            ),
          type: FIXED_OPTIONS[column.type] ? "select" : column.type,
          options: FIXED_OPTIONS[column.type] ?? column.options ?? [],
          filter: (
            <FilterSearch
              query={filter.data}
              setQuery={handleFilterChange}
              columnName={column.name}
            />
          ),
          filtering: filter?.data?.[column.name],
        };
      }),
      {
        accessor: "actions",
        textAlign: "right",
        width: 200,
        editable: false,
        render: (row) => (
          <TableActions>
            <TableAction
              label={
                // The campaign goes to the ticket's own email, with or
                // without an account behind it.
                !row.email
                  ? "Ticket has no email"
                  : hasAvailableCampaigns
                    ? `Send campaign to ${row.firstName} ${row.lastName}`
                    : "No campaigns available"
              }
              icon={IconMailForward}
              color="violet"
              disabled={!row.email || !hasAvailableCampaigns}
              onClick={() =>
                openSendCampaignModal({
                  eventId: currentEventId,
                  ticket: row,
                })
              }
            />
            <TableAction
              label={`Print ticket for ${row.firstName} ${row.lastName}`}
              icon={IconPrinter}
              color="cyan"
              onClick={() => handlePrint(row)}
            />
            <EditAction to={`${row.id}`} />
            <DeleteAction onDelete={() => confirmDelete({ id: row.id })} />
          </TableActions>
        ),
      },
    ];

    const { effectiveColumns, resetColumnsOrder } = useDataTableColumns({
      key: `tickets-table-v3-${currentEventId}`,
      columns: tableColumns,
    });

    return (
      <>
        <Group justify="flex-end" pr="xs" my="sm">
          <Tooltip label="Reset column order">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={() => {
                resetColumnsOrder();
              }}
            >
              <IconRefresh />
            </ActionIcon>
          </Tooltip>
        </Group>

        <InlineEditableTable
          ref={ref}
          data={data?.data}
          renderHeader={() => null}
          createEmptyRow={() => ({
            id: "temp",
            firstName: filter.data?.firstName || "",
            lastName: filter.data?.lastName || "",
            email: filter.data?.email || "",
            companyName: filter.data?.companyName || "",
            category: defaultCategoryTag ? [String(defaultCategoryTag.id)] : [],
            tags: defaultCategoryTag ? [defaultCategoryTag] : [],
            // Add custom fields to the empty row
            ...customColumns?.reduce((acc, column) => {
              acc[column.name] = filter.data?.[column.name] || "";
              return acc;
            }, {}),
          })}
          validateRow={(row) => {
            const errors = {};

            if (!row.firstName) {
              errors.firstName = "First name is required";
            }

            if (!row.lastName) {
              errors.lastName = "Last name is required";
            }

            if (row.email && !isValidEmail(row.email)) {
              errors.email = "Invalid email format";
            }

            return errors;
          }}
          onRowCreate={(row) => saveRowAdd(row)}
          onRowChange={(row) => saveRowEdit(row)}
          columns={effectiveColumns}
          resizable={true}
          tableProps={{
            storeColumnsKey: `tickets-table-v3-${currentEventId}`,
            pinLastColumn: true,
            page: pagination.data.page,
            onPageChange: (page) =>
              pagination.setPagination({ ...pagination.data, page }),
            sortStatus: sorting.data,
            onSortStatusChange: sorting.setSorting,
            fetching: isPending,
            recordsPerPage: data?.pagination.perPage,
            totalRecords: data?.pagination.total,
          }}
        />
      </>
    );
  }
);

// The ticket's one app status.
function ClaimCell({ row }) {
  return <AppStatusBadge ticket={row} />;
}

// Shows the linked account's value under the holder's only when they differ
function HolderCell({ value, accountValue }) {
  const differs =
    accountValue != null &&
    String(accountValue).toLowerCase() !== String(value ?? "").toLowerCase();

  return (
    <>
      <Text size="sm">{value}</Text>
      {differs && (
        <Text size="xs" c="dimmed">
          Account: {accountValue}
        </Text>
      )}
    </>
  );
}

function FilterSearch({ query, setQuery, columnName }) {
  // Transform camel case to readable text
  const camelToReadableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <TextInput
      label="Tickets"
      description={`Show tickets whose ${camelToReadableColumnName} includes the specified text`}
      placeholder="Search tickets..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query?.[columnName] || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery({ [columnName]: e.currentTarget.value });
      }}
    />
  );
}

function CategoryFilter({ query, setQuery, tags, isPending }) {
  if (isPending) {
    <Text>Loading tags...</Text>;
  }

  return (
    <Select
      label="Category"
      description="Filter tickets by category"
      placeholder="Filter by category"
      data={tags}
      value={query?.category}
      onChange={(value) => {
        setQuery({ category: value });
      }}
      onClear={() => setQuery(null)}
      clearable
    />
  );
}

export default TicketsTable;
